/** @typedef {import("chart.js").ChartOptions} ChartOptions */

const SMALL_SCREEN_WIDTH = 680;
const BG_COLOR = "#fff";

/**
 * `getNativeOptionsChart` function to generate the native options for the chart.
 * @param {{[key: string]: number}} data - The data for the chart
 * @returns {ChartOptions} The options for the chart
 */
export const getNativeOptionsChart = (data) => {
  const {
    barPercentage,
    barBorderRadius,
    barBorderWidth,
    barMaxBarThickness,
    barMinBarLength,
    categoryPercentage,
    maintainAspectRatio = false,
    ticksFont,
  } = data;

  return {
    responsive: true,
    maintainAspectRatio,

    barPercentage: function (context) {
      const chart = context.chart;
      const width = chart.width;

      const smPercentage = barPercentage?.sm ?? 0.85;
      const lgPercentage = barPercentage?.lg ?? 0.9;

      if (width < SMALL_SCREEN_WIDTH) return smPercentage;
      return lgPercentage;
    },

    interaction: {
      mode: "index",
      intersect: false,
    },

    scales: {
      x: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
          font: {
            size: function (context) {
              const chart = context.chart;
              const width = chart.width;
              const smSize = ticksFont?.sm ?? 10;
              const lgSize = ticksFont?.lg ?? 14;

              if (width < SMALL_SCREEN_WIDTH) return smSize;
              return lgSize;
            },
          },
        },
        stacked: true,
      },

      y: {
        grid: {
          display: false,
        },
        border: {
          display: false,
        },
        ticks: {
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
          font: {
            size: function (context) {
              const chart = context.chart;
              const width = chart.width;
              const smSize = ticksFont?.sm ?? 10;
              const lgSize = ticksFont?.lg ?? 14;

              if (width < SMALL_SCREEN_WIDTH) return smSize;
              return lgSize;
            },
          },
        },
        stacked: true,
      },
    },

    datasets: {
      bar: {
        categoryPercentage: function (context) {
          const chart = context.chart;
          const width = chart.width;

          const smPercentage = categoryPercentage?.sm ?? 0.8;
          const lgPercentage = categoryPercentage?.lg ?? 0.9;

          if (width < SMALL_SCREEN_WIDTH) return smPercentage;
          return lgPercentage;
        },
        skipNull: true,
        maxBarThickness: function (context) {
          const chart = context.chart;
          const width = chart.width;

          const smThickness = barMaxBarThickness?.sm ?? 16;
          const lgThickness = barMaxBarThickness?.lg ?? 24;

          if (width < SMALL_SCREEN_WIDTH) return smThickness;
          return lgThickness;
        },

        borderWidth: function (context) {
          const chart = context.chart;
          const width = chart.width;

          const smWidth = barBorderWidth?.sm ?? 1;
          const lgWidth = barBorderWidth?.lg ?? 2;

          const borderWidth = width < SMALL_SCREEN_WIDTH ? smWidth : lgWidth;

          return {
            top: borderWidth,
            bottom: borderWidth,
          };
        },

        backgroundColor: BG_COLOR,
        borderColor: BG_COLOR,
        hoverBorderColor: BG_COLOR,
        borderSkipped: false,

        borderRadius: function (context) {
          const chart = context.chart;
          const width = chart.width;

          const smRadius = barBorderRadius?.sm ?? 6;
          const lgRadius = barBorderRadius?.lg ?? 8;

          const radius = width < SMALL_SCREEN_WIDTH ? smRadius : lgRadius;

          return {
            topLeft: radius,
            topRight: radius,
            bottomLeft: radius,
            bottomRight: radius,
          };
        },

        minBarLength: function (context) {
          const chart = context.chart;
          const width = chart.width;

          const smLength = barMinBarLength?.sm ?? 0;
          const lgLength = barMinBarLength?.lg ?? 0;

          if (width < SMALL_SCREEN_WIDTH) return smLength;
          return lgLength;
        },
      },
    },

    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: false,
        position: "nearest",
        external: () => {},
      },
    },
  };
};
